import React, { useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { GROUP_ORDERS_URL } from "@API/APIRoutes";
import usePaginate from "@Hooks/usePaginate";
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import GenericPopup from "@Components/Popup/Popup";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { encryptId } from "@Utils/SecurityFunctions";

const TagsContainer = styled(Box)(() => ({
  display: "flex",
  gap: 4,
  flexWrap: "wrap",
  alignItems: "center",
  height: "100%",
}));

const TagItem = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: "0.75rem",
  lineHeight: 1.4,
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const OrdersPopupList = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
}));

const OrderPopupRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  padding: "10px 12px",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
}));

const DATE_FIELDS = [
  "acidCreationDate",
  "pfiReceivalDate",
  "form4SendingDate",
  "form4ReceivalDate",
  "maturityDate",
  "invoiceDate",
  "bolDate",
  "shippedOnboardDate",
  "releaseDate",
  "actualArrivalDate",
  "expectedArrivalDate",
  "customsReleaseDate",
];

const NUMERIC_FIELDS = [
  "invoiceAmount",
  "grossWeight",
  "netWeight",
  "poAmount",
  "fod",
  "maturityPeriod",
  "container20",
  "container40",
  "pallets",
  "boxes",
  "drums",
];

// Keep the same column-chooser rules used by the pending orders listing.
const NON_HIDEABLE_FIELDS = new Set([
  "wayOfShipping",
  "hsCode",
  "origin",
  "relativeCompany",
  "pfiReceivalDate",
  "grossWeight",
  "poAmount",
  "fod",
  "maturityPeriod",
  "invoiceDate",
  "netWeight",
  "container20",
  "container40",
  "pallets",
  "boxes",
  "drums",
]);

const STRING_FIELDS = [
  "currentState",
  "poNumber",
  "supplier",
  "planner",
  "agent",
  "category",
  "materials",
  "wayOfShipping",
  "origin",
  "relativeCompany",
  "acidNumber",
  "bank",
  "maturityType",
  "incoTerm",
  "invoiceNumber",
  "invoiceCurrency",
  "bolNumber",
  "portOfArrival",
  "shippingLine",
  "shippingPort",
  "transactionType",
  "loadability",
  "hsCode",
];

const formatDate = (date) => {
  if (!date) return "";

  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime())
    ? ""
    : parsed.toLocaleDateString("en-GB");
};

// Grouped date fields are displayed as comma-separated en-GB date strings.
// MUI date columns still need one Date object as their underlying value.
const getGroupedDateValue = (value) => {
  const firstDate = toDisplayString(value).split(",")[0].trim();
  if (!firstDate) return null;

  const enGbDate = firstDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const parsed = enGbDate
    ? new Date(
        Number(enGbDate[3]),
        Number(enGbDate[2]) - 1,
        Number(enGbDate[1])
      )
    : new Date(firstDate);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatState = (state) => {
  if (!state) return "";

  return state
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const toDisplayString = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  return String(value).trim();
};

const joinValues = (values) =>
  [...new Set(values.map(toDisplayString).filter(Boolean))].join(", ");

const sumValues = (values) => {
  const numericValues = values
    .map((value) => {
      if (value === null || value === undefined || value === "") return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    })
    .filter((value) => value !== null);

  if (!numericValues.length) return null;

  return numericValues.reduce((sum, value) => sum + value, 0);
};

const buildGroupedRow = (group) => {
  const orders = Array.isArray(group?.orders) ? group.orders : [];

  return {
    id: group?.groupId,
    groupId: group?.groupId ?? "",
    ordersCount: orders.length,
    poNumber: joinValues(orders.map((order) => order.poNumber)),
    supplier: joinValues(
      orders.map((order) => order.supplier?.supplierName || order.supplier),
    ),
    planner: joinValues(
      orders.map((order) => order.planner?.plannerName || order.planner),
    ),
    agent: joinValues(
      orders.map((order) => order.agent?.agentName || order.agent),
    ),
    category: joinValues(
      orders.map((order) => order.category?.categoryName || order.category),
    ),
    materials: joinValues(
      orders.flatMap((order) =>
        Array.isArray(order.materials)
          ? order.materials.map((material) => material?.materialName)
          : [],
      ),
    ),
    currentState: joinValues(orders.map((order) => formatState(order.state))),
    wayOfShipping: joinValues(orders.map((order) => order.wayOfShipping)),
    origin: joinValues(orders.map((order) => order.origin)),
    relativeCompany: joinValues(orders.map((order) => order.relativeCompany)),
    acidNumber: joinValues(orders.map((order) => order.acidNumber)),
    acidCreationDate: joinValues(
      orders.map((order) => formatDate(order.acidCreationDate)),
    ),
    pfiReceivalDate: joinValues(
      orders.map((order) => formatDate(order.pfiReceivalDate)),
    ),
    form4SendingDate: joinValues(
      orders.map((order) => formatDate(order.form4SendingDate)),
    ),
    form4ReceivalDate: joinValues(
      orders.map((order) => formatDate(order.form4ReceivalDate)),
    ),
    bank: joinValues(orders.map((order) => order.bank)),
    maturityType: joinValues(orders.map((order) => order.maturityType)),
    incoTerm: joinValues(orders.map((order) => order.incoTerm)),
    maturityDate: joinValues(
      orders.map((order) => formatDate(order.maturityDate)),
    ),
    invoiceNumber: joinValues(orders.map((order) => order.invoiceNumber)),
    invoiceAmount: sumValues(orders.map((order) => order.invoiceAmount)),
    invoiceCurrency: joinValues(orders.map((order) => order.invoiceCurrency)),
    invoiceDate: joinValues(
      orders.map((order) => formatDate(order.invoiceDate)),
    ),
    bolNumber: joinValues(orders.map((order) => order.bolNumber)),
    bolDate: joinValues(orders.map((order) => formatDate(order.bolDate))),
    portOfArrival: joinValues(orders.map((order) => order.portOfArrival)),
    shippingLine: joinValues(
      orders.map(
        (order) => order.shippingLine?.shippingLineName || order.shippingLine,
      ),
    ),
    shippingPort: joinValues(orders.map((order) => order.shippingPort)),
    shippedOnboardDate: joinValues(
      orders.map((order) => formatDate(order.shippedOnboardDate)),
    ),
    releaseDate: joinValues(
      orders.map((order) => formatDate(order.releaseDate)),
    ),
    actualArrivalDate: joinValues(
      orders.map((order) => formatDate(order.actualArrivalDate)),
    ),
    expectedArrivalDate: joinValues(
      orders.map((order) => formatDate(order.expectedArrivalDate)),
    ),
    customsReleaseDate: joinValues(
      orders.map((order) => formatDate(order.customsReleaseDate)),
    ),
    transactionType: joinValues(orders.map((order) => order.transactionType)),
    loadability: joinValues(orders.map((order) => order.loadability)),
    hsCode: joinValues(orders.map((order) => order.hsCode)),
    grossWeight: sumValues(orders.map((order) => order.grossWeight)),
    netWeight: sumValues(orders.map((order) => order.netWeight)),
    poAmount: sumValues(orders.map((order) => order.poAmount)),
    fod: sumValues(orders.map((order) => order.fod)),
    maturityPeriod: sumValues(orders.map((order) => order.maturityPeriod)),
    container20: sumValues(orders.map((order) => order.container20)),
    container40: sumValues(orders.map((order) => order.container40)),
    pallets: sumValues(orders.map((order) => order.pallets)),
    boxes: sumValues(orders.map((order) => order.boxes)),
    drums: sumValues(orders.map((order) => order.drums)),
    orders,
  };
};

function GroupedOrdersListing() {
  const theme = themeCreator();
  const navigate = useNavigate();
  const { loading: pageLoading, getPage } = usePaginate();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const stringOperators = getGridStringOperators().filter((operator) =>
    [
      "contains",
      "doesNotContain",
      "equals",
      "doesNotEqual",
      "startsWith",
      "endsWith",
      "isEmpty",
      "isNotEmpty",
    ].includes(operator.value),
  );

  const numberOperators = getGridNumericOperators().filter((operator) =>
    ["=", "!=", ">", ">=", "<", "<=", "isEmpty", "isNotEmpty"].includes(
      operator.value,
    ),
  );

  const dateOperators = getGridDateOperators();

  const fetchPage = (queryOptions, filterModel) => {
    const filterItems = (filterModel?.items || []).map((filterItem) => {
      if (filterItem && typeof filterItem.value === "string") {
        return { ...filterItem, value: filterItem.value.trim() };
      }

      return filterItem;
    });

    getPage(
      GROUP_ORDERS_URL,
      "Failed to Fetch Grouped Orders Trying Again!",
      queryOptions,
      filterItems,
    ).then((data) => {
      const groupedRows = Array.isArray(data?.content)
        ? data.content.map(buildGroupedRow)
        : [];

      setRows(groupedRows);
      setRowCount(data?.totalElements || 0);
    });
  };

  const handleNavigateToOrder = (orderId) => {
    navigate(DefaultRoutes.orderInfo.replace(":orderId", encryptId(orderId)));
  };

  const selectedGroupOrders = Array.isArray(selectedGroup?.orders)
    ? selectedGroup.orders
    : [];

  const columns = [
    {
      field: "ordersCount",
      headerName: "Orders Count",
      flex: 1.1,
      type: "number",
      filterOperators: numberOperators,
    },
    ...STRING_FIELDS.map((field) => ({
      field,
      headerName:
        field === "poNumber"
          ? "PO Number"
          : field === "currentState"
            ? "Status"
            : field === "wayOfShipping"
              ? "Way Of Shipping"
              : field === "relativeCompany"
                ? "Company"
                : field === "acidNumber"
                  ? "ACID Number"
                  : field === "maturityType"
                    ? "Maturity Type"
                    : field === "incoTerm"
                      ? "Inco Term"
                      : field === "invoiceNumber"
                        ? "Invoice Number"
                        : field === "invoiceCurrency"
                          ? "Invoice Currency"
                          : field === "bolNumber"
                            ? "BOL Number"
                            : field === "portOfArrival"
                              ? "Port of Arrival"
                              : field === "shippingLine"
                                ? "Shipping Line"
                                : field === "shippingPort"
                                  ? "Shipping Port"
                                  : field === "transactionType"
                                    ? "Transaction Type"
                                    : field === "hsCode"
                                      ? "HS Code"
                                      : field.charAt(0).toUpperCase() +
                                        field
                                          .slice(1)
                                          .replace(/([A-Z])/g, " $1")
                                          .trim(),
      flex: field === "materials" ? 2.4 : 2,
      type: "string",
      filterOperators: stringOperators,
      hideable: !NON_HIDEABLE_FIELDS.has(field),
      renderCell:
        field === "materials"
          ? (params) => {
              const items = params.value
                ? params.value.split(", ").filter(Boolean)
                : [];

              return (
                <TagsContainer>
                  {items.map((item) => (
                    <TagItem key={item}>{item}</TagItem>
                  ))}
                </TagsContainer>
              );
            }
          : undefined,
    })),
    ...DATE_FIELDS.map((field) => ({
      field,
      headerName:
        field === "acidCreationDate"
          ? "ACID Creation Date"
          : field === "pfiReceivalDate"
            ? "PFI Receival Date"
            : field === "form4SendingDate"
              ? "Form4 Sent"
              : field === "form4ReceivalDate"
                ? "Form4 Received"
                : field === "maturityDate"
                  ? "Maturity Date"
                  : field === "invoiceDate"
                    ? "Invoice Date"
                    : field === "bolDate"
                      ? "BOL Date"
                      : field === "shippedOnboardDate"
                        ? "Shipped On Board"
                        : field === "releaseDate"
                          ? "Release Date"
                          : field === "actualArrivalDate"
                            ? "Actual Arrival"
                            : field === "expectedArrivalDate"
                              ? "Expected Arrival"
                              : field === "customsReleaseDate"
                                ? "Customs Released"
                                : field,
      flex: 2,
      type: "date",
      filterOperators: dateOperators,
      hideable: !NON_HIDEABLE_FIELDS.has(field),
      valueGetter: (_value, row) => getGroupedDateValue(row?.[field]),
      valueFormatter: (_value, row) => row?.[field] || "",
      // Group rows display all dates from their orders as a comma-separated
      // value, while filtering is performed by the grouped-orders endpoint.
      renderCell: (params) => params.row?.[field] || "",
    })),
    ...NUMERIC_FIELDS.map((field) => ({
      field,
      headerName:
        field === "invoiceAmount"
          ? "Invoice Amount"
          : field === "grossWeight"
            ? "Gross Weight"
            : field === "netWeight"
              ? "Net Weight"
              : field === "poAmount"
                ? "PO Amount"
                : field === "fod"
                  ? "FOD"
                  : field === "maturityPeriod"
                    ? "Maturity Period"
                    : field === "container20"
                      ? "20ft Containers"
                      : field === "container40"
                        ? "40ft Containers"
                        : field.charAt(0).toUpperCase() +
                          field
                            .slice(1)
                            .replace(/([A-Z])/g, " $1")
                            .trim(),
      flex: 1.5,
      type: "number",
      filterOperators: numberOperators,
      hideable: !NON_HIDEABLE_FIELDS.has(field),
    })),
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      hideable: false,
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="View Group Orders">
          <IconButton
            color="info"
            onClick={() => setSelectedGroup(params.row)}
            size="small"
          >
            <VisibilityRoundedIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const initialColumnVisibilityModel = {
    groupId: false,
    ordersCount: false,
    poNumber: true,
    supplier: true,
    planner: true,
    materials: true,
    agent: true,
    category: true,
    currentState: true,
    invoiceAmount: false,
    invoiceNumber: false,
    invoiceCurrency: false,
    invoiceDate: false,
    wayOfShipping: false,
    origin: false,
    relativeCompany: false,
    acidNumber: false,
    acidCreationDate: false,
    pfiReceivalDate: false,
    bank: false,
    maturityType: false,
    incoTerm: false,
    maturityDate: false,
    bolNumber: false,
    bolDate: false,
    portOfArrival: false,
    shippingLine: false,
    shippingPort: false,
    shippedOnboardDate: false,
    releaseDate: false,
    actualArrivalDate: false,
    expectedArrivalDate: false,
    customsReleaseDate: false,
    transactionType: false,
    loadability: false,
    hsCode: false,
    grossWeight: false,
    netWeight: false,
    poAmount: false,
    fod: false,
    maturityPeriod: false,
    container20: false,
    container40: false,
    pallets: false,
    boxes: false,
    form4ReceivalDate: false,
    form4SendingDate: false,
    drums: false,
    actions: true,
  };

  return (
    <>
      <ListingPageTemplate
        title="Group View"
        PageIcon={<ViewWeekRoundedIcon color="white" fontSize="large" />}
        iconColor={theme.palette.primary.main}
        tableProps={{
          columns,
          disableRowSelectionOnClick: true,
          disableColumnMenu: true,
          initialColumnVisibilityModel,
          rowCount,
          rows,
          isLoading: pageLoading,
          fetchPage,
          sortBy: "groupId",
          enableExport: false,
        }}
        serverPagination={true}
      />
      <GenericPopup
        open={Boolean(selectedGroup)}
        title={`Group Orders (${selectedGroupOrders.length})`}
        icon={<VisibilityRoundedIcon color="white" fontSize="large" />}
        maxWidth="sm"
        content={
          selectedGroupOrders.length ? (
            <OrdersPopupList>
              {selectedGroupOrders.map((order, index) => {
                const orderId = order?.id || order?.orderId;
                const orderLabel =
                  order?.poNumber || order?.orderNumber || `Order ${index + 1}`;

                return (
                  <OrderPopupRow key={orderId || index}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>
                        {orderLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {joinValues([
                          orderId ? `ID: ${orderId}` : "",
                          order?.supplier?.supplierName || order?.supplier,
                        ])}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<ArrowRightAltIcon />}
                      disabled={!orderId}
                      onClick={() => handleNavigateToOrder(orderId)}
                    >
                      Details
                    </Button>
                  </OrderPopupRow>
                );
              })}
            </OrdersPopupList>
          ) : (
            <Typography color="text.secondary">
              No orders found in this group.
            </Typography>
          )
        }
        buttons={[
          {
            text: "Close",
            variant: "outlined",
            color: "primary",
            onClick: () => setSelectedGroup(null),
          },
        ]}
      />
    </>
  );
}

export default GroupedOrdersListing;
